export const ADMIN_REVIEW_CHECKLIST_VERSION = 1;
const STORAGE_KEY_PREFIX = "meet-the-world:admin-review-checks:";

type AdminReviewChecklistPayload = {
  version: typeof ADMIN_REVIEW_CHECKLIST_VERSION;
  checkedItemIds: string[];
};

export function getAdminReviewChecklistStorageKey(submissionId: string): string {
  return `${STORAGE_KEY_PREFIX}${submissionId}`;
}

export function parseAdminReviewChecklist(raw: string | null): Set<string> {
  if (!raw) return new Set();

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isChecklistPayload(parsed)) return new Set();
    return new Set(parsed.checkedItemIds.filter((itemId) => typeof itemId === "string" && itemId.length > 0));
  } catch {
    return new Set();
  }
}

export function toggleAdminReviewChecklistItems(checkedItemIds: ReadonlySet<string>, itemIds: readonly string[]): Set<string> {
  const next = new Set(checkedItemIds);
  const uncheckAll = itemIds.every((itemId) => checkedItemIds.has(itemId));
  for (const itemId of itemIds) {
    if (uncheckAll) next.delete(itemId);
    else next.add(itemId);
  }
  return next;
}

type ReviewRow = { id: string; label: string; value: string };
export type AdminReviewRowGroup<Row extends ReviewRow> = { id: string; title: string | null; rows: Array<{ row: Row; label: string }> };

const LINE_CHECK_ROW_IDS = new Set(["policy.bookingQuestionChecklist"]);
const REPEATER_ROW_ID = /^(.+\.(\d+))\.[^.]+$/;
const GROUP_LABEL_SEPARATOR = " · ";

export function groupAdminReviewRows<Row extends ReviewRow>(rows: readonly Row[]): AdminReviewRowGroup<Row>[] {
  const groups: AdminReviewRowGroup<Row>[] = [];
  const repeaterGroups = new Map<string, AdminReviewRowGroup<Row>>();
  for (const row of rows) {
    const last = groups[groups.length - 1];
    const [, repeaterId, index] = row.id.match(REPEATER_ROW_ID) ?? [];
    const title = repeaterId ? repeaterGroupTitle(row.label, Number(index) + 1) : null;
    if (repeaterId && title) {
      const entry = { row, label: row.label.slice(title.length + GROUP_LABEL_SEPARATOR.length) };
      const existing = repeaterGroups.get(repeaterId);
      if (existing) {
        existing.rows.push(entry);
      } else {
        const group = { id: repeaterId, title, rows: [entry] };
        repeaterGroups.set(repeaterId, group);
        groups.push(group);
      }
    } else if (last?.title === null) {
      last.rows.push({ row, label: row.label });
    } else {
      groups.push({ id: `rows:${row.id}`, title: null, rows: [{ row, label: row.label }] });
    }
  }
  return groups;
}

function repeaterGroupTitle(label: string, entryNumber: number): string | null {
  const marker = ` ${entryNumber}${GROUP_LABEL_SEPARATOR}`;
  const markerIndex = label.indexOf(marker);
  return markerIndex > 0 ? label.slice(0, markerIndex + ` ${entryNumber}`.length) : null;
}

export function getAdminReviewRowLines(row: ReviewRow): Array<{ id: string; text: string }> | null {
  if (!LINE_CHECK_ROW_IDS.has(row.id)) return null;
  const lines = row.value.split("\n").map((text) => text.trim()).filter(Boolean);
  if (!lines.length) return null;
  const occurrences = new Map<string, number>();
  return lines.map((text) => {
    const occurrence = (occurrences.get(text) ?? 0) + 1;
    occurrences.set(text, occurrence);
    return { id: occurrence === 1 ? `${row.id}#${text}` : `${row.id}#${text}#${occurrence}`, text };
  });
}

export function getAdminReviewRowItemIds(row: ReviewRow): string[] {
  return getAdminReviewRowLines(row)?.map((line) => line.id) ?? [row.id];
}

export function serializeAdminReviewChecklist(checkedItemIds: ReadonlySet<string>): string {
  const payload: AdminReviewChecklistPayload = {
    version: ADMIN_REVIEW_CHECKLIST_VERSION,
    checkedItemIds: [...checkedItemIds],
  };
  return JSON.stringify(payload);
}

function isChecklistPayload(value: unknown): value is AdminReviewChecklistPayload {
  if (!isRecord(value)) return false;
  return value.version === ADMIN_REVIEW_CHECKLIST_VERSION && Array.isArray(value.checkedItemIds);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
